import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import '../../styles/CustomNavigation.css';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import PubSub from 'pubsub-js';
import { EVENT_TOPIC_CONTACT_DETAILS_VISIBILITY, RESTAURANT } from '@src/Constants';
// import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
// import { ReactComponent as ChatIcon } from '../../assets/images/messages.svg';
// import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MailIcon from '@mui/icons-material/Mail';
import Backdrop from '@mui/material/Backdrop';
import { useNavigate, useParams } from 'react-router-dom';
import {
	ShoppingCartIcon,
	SettingsIcon,
	MessageIcon,
	PriceCatalogIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	SupportIcon,
	DashboardIcon,
	CatalogsIcon,
	ProductIcon,
	MarketplaceIcon,
	ArrowDown,
	ArrowUp,
	AiInsightsIcon,
	FinanceIcon,
} from '../Icons/index';
import restaurantLogo from '../../assets/images/restaurant__logo.png';
import logo from '../../assets/Logo/ylPartsLogo.png';
import logo_2 from '../../assets/Logo/yl-logo.png';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { IsMobileWidth } from '../MediaQueryUtils';
import { setToggleMenu, setIsDrawerOpen } from '@src/store/reducers/UIReducer';
import { Badge, MenuItem } from '@mui/material';
import CustomMenu from '../CustomMenuItem/CustomMenu';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import CustomTextButton from '../CustomButton/CustomTextButton';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	// justifyContent: 'center',
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

const DrawerControllers = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));
const DrawerFooter = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(0, 1),
	'& .navigation__footer': {
		display: 'flex',
		background: '#FDFDFD',
		width: '100%',
		// margin: '10px 2px',
		padding: '10px',
		alignItems: 'center',
		border: '1.5px solid #F1F1F1',
		borderRadius: '5px',
	},
	'& .remove__spacing': {
		padding: '10px 5px !important',
	},
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, isActive }: any) => ({
	minHeight: '44px !important',
	height: '44px !important',
	margin: '8px',
	borderRadius: '5px',
	backgroundColor: isActive ? '#FFE599' : 'transparent',
	color: isActive ? '#1A1D1F' : '#7D7D7D',
	':hover': { color: '#1A1D1F', backgroundColor: '#FFE599' },
	// ':hover '
	...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
	open?: boolean;
}

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
	({ theme, open }) => ({
		width: drawerWidth,
		flexShrink: 0,
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		'& .MuiDrawer-paper .MuiList-root .MuiListItem-root .MuiButtonBase-root-MuiListItemButton-root:hover':
			{
				color: '#1A1D1F',
				borderRadius: '5px',
				backgroundColor: '#FFE599',
			},
		...(open && {
			...openedMixin(theme),
			'& .MuiDrawer-paper': openedMixin(theme),
		}),
		...(!open && {
			...closedMixin(theme),
			'& .MuiDrawer-paper': closedMixin(theme),
		}),
	})
);

const StyledNestedListContainer = styled('div')(({ theme }) => ({
	position: 'relative',
	marginLeft: '33px',
	'::before': {
		content: '""',
		position: 'absolute',
		top: '0',
		left: '0',
		bottom: '0',
		width: '2px',
		backgroundColor: '#D9D9D9',
	},
}));

const StyledNestedListItem = styled('div')(({ active }: any) => ({
	marginLeft: '10px',
	padding: '8px 12px',
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	color: active ? 'var(--default-text-color)' : 'var(--lighter-text-color)',
	fontFamily: 'Nunito Sans',
	fontSize: '12px',
	fontWeight: active ? '700' : '600',
	cursor: 'pointer',
	transition: 'color 0.3s, font-weight 0.3s',
}));

const StyledMenuItem = styled(MenuItem)(({ active }: any) => ({
	color: active ? 'var(--default-text-color)' : 'var(--lighter-text-color)',
	fontFamily: 'Nunito Sans',
	fontSize: '12px',
	fontWeight: active ? '700' : '600',
	cursor: 'pointer',
	transition: 'color 0.3s, font-weight 0.3s',
}));

interface CustomNavigationProps {
	open: boolean;
	setOpen: (data: boolean) => void;
	setOrderBook: (data: boolean) => void;
	supplier: {};
	messageCount: number;
}

export default function CustomNavigation({
	open,
	setOpen,
	supplier,
	setOrderBook,
	messageCount = 0,
}: CustomNavigationProps) {
	const theme = useTheme();

	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const [hover, isHover] = React.useState(-1);
	const [activeIndex, setActiveIndex] = React.useState(0);
	const [navListData, setNavListData] = React.useState([]);
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const restaurant: any = useAppSelector((state) => state?.restaurant?.value);
	const isDrawerOpen: any = useAppSelector((state) => state?.UI?.isDrawerOpen);
	const branchId = restaurant?.default?.id;
	const toggleDrawerOnMobileWidth = () => {
		dispatch(setToggleMenu(false));
	};
	let navigate = useNavigate();
	// const { id: branchId } = useParams();
	const dispatch = useAppDispatch();
	const isMobileWidth = IsMobileWidth();

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const isOptionActive = (menuOption: string) => {
		const url = window?.location?.href;
		if (url?.includes(menuOption)) {
			return true;
		}
		return false;
	};

	const handleDrawerOpen = () => {
		setOpen(true);
		PubSub.publish(EVENT_TOPIC_CONTACT_DETAILS_VISIBILITY, false);
		dispatch(setIsDrawerOpen(true));
	};

	const handleDrawerClose = () => {
		setOpen(false);
		dispatch(setIsDrawerOpen(false));
	};

	const handleIsHover = (index: number, key: number) => index === key;

	//Add all paths here for active tab display
	const getPageFromUrl = () => {
		const page: string[] = location?.pathname.split('/');
		const pageMap: any = {
			dashboard: 'dashboard',
			orders: 'orders',
			catalogs: 'catalogs',
			productCatalog: 'productCatalog',
			marketplace: 'marketplace',
			rewards: 'rewards',
			settings: 'settings',
			'ai-insights': 'ai-insights',
			customers: 'customers',
			finance: 'finance',
		};

		const currentPage = page.find((path) => path in pageMap);
		return currentPage ? pageMap[currentPage] : false;
	};

	const isActiveTab = (item: any): boolean => {
		const activePage = getPageFromUrl();
		const itemPath = item?.path || '';

		if (activePage) {
			return itemPath.split('/').includes(activePage);
		}

		return location?.pathname.includes(itemPath);
	};

	React.useEffect(() => {
		let navListData = [];
		if (currentUser?.type === 'Restaurant') {
			navListData = [
				{
					id: 1,
					text: 'Suppliers',
					onclick: (event: any) => {
						navigate(`/restaurants/${branchId}`);
						setOrderBook(false);
						if (isMobileWidth) toggleDrawerOnMobileWidth();
					},
					path: `/restaurants`,
					icon: MessageIcon,
				},
				{
					id: 2,
					text: 'Orders',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/restaurants/${branchId}/orders/allOrders`);
						}
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/orders`,
					icon: ShoppingCartIcon,
					showOptions: isOptionActive('orders'),
					openNestedMenu: isOptionActive('orders'),
					nestedList: [
						{
							id: 1,
							text: 'All Orders',
							onclick: () => {
								navigate(`/restaurants/${branchId}/orders/allOrders`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('orders/allOrders'),
							path: `/orders`,
						},
						{
							id: 2,
							text: 'Accept Delivery',
							onclick: () => {
								navigate(`/restaurants/${branchId}/orders/acceptDelivery`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('orders/acceptDelivery'),
							path: `/orders`,
						},
					],
				},
				// {
				// 	id: 3,
				// 	text: 'Marketplace',
				// 	onclick: (event: any, id: number) => {
				// 		setNavListData((prevState: any) => {
				// 			return prevState?.map((item: any) => {
				// 				if (item?.id === id) {
				// 					return { ...item, openNestedMenu: !item?.openNestedMenu };
				// 				}
				// 				return item;
				// 			});
				// 		});
				// 		if (!isMobileWidth) {
				// 			navigate(`/marketplace/explore`);
				// 		}
				// 	},
				// 	path: `/marketplace`,
				// 	icon: MarketplaceIcon,
				// 	showOptions: isOptionActive('marketplace'),
				// 	openNestedMenu: isOptionActive('marketplace'),
				// 	nestedList: [
				// 		{
				// 			text: 'Explore Market',
				// 			onclick: () => {
				// 				navigate(`/marketplace/explore`);
				// 				if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 			},
				// 			active: isOptionActive('explore'),
				// 			path: `/marketplace`,
				// 		},
				// 		{
				// 			text: 'Browse Suppliers',
				// 			onclick: () => {
				// 				navigate(`/marketplace/browseSuppliers`);
				// 				if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 			},
				// 			active: isOptionActive('browseSuppliers'),
				// 			path: `/marketplace`,
				// 		},
				// 	],
				// },
				{
					id: 4,
					text: 'Settings',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/restaurants/${branchId}/settings/general`);
						}
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/settings`,
					icon: SettingsIcon,
					showOptions: isOptionActive('settings'),
					openNestedMenu: isOptionActive('settings'),
					nestedList: [
						{
							id: 1,
							text: 'General Settings',
							onclick: () => {
								navigate(`/restaurants/${branchId}/settings/general`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('general'),
							path: `/settings`,
						},
						{
							id: 2,
							text: 'Dealer Settings',
							onclick: () => {
								navigate(`/restaurants/${branchId}/settings/branch`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('settings/branch'),
							path: `/settings`,
						},
						{
							id: 3,
							text: 'Teams & Permissions',
							onclick: () => {
								navigate(`/restaurants/${branchId}/settings/teams&permissions?page=1&limit=25`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('teams') || isOptionActive('permissions'),
							path: `/settings`,
						},
						{
							id: 4,
							text: 'Integrations',
							onclick: () => {
								navigate(`/restaurants/${branchId}/settings/integrations`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('integrations'),
							path: `/settings`,
						},
					],
				},
				{
					id: 8,
					text: 'Rewards',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/restaurants/${branchId}/rewards/dashboard`);
						}
						// if (isMobileWidth) toggleDrawerOnMobileWidth();
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/rewards`,
					icon: ShoppingCartIcon,
					showOptions: isOptionActive('rewards'),
					openNestedMenu: isOptionActive('rewards'),
					nestedList: [
						{
							id: 1,
							text: 'Rewards Dashboard',
							onclick: () => {
								navigate(`/restaurants/${branchId}/rewards/dashboard`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('rewards/dashboard'),
							path: `/rewards`,
						},
					],
				},
				// {
				// 	id: 5,
				// 	text: 'Insights by AI',
				// 	onclick: () => {
				// 		navigate(`/restaurants/${branchId}/ai-insights`);
				// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 	},
				// 	path: `/ai-insights`,
				// 	icon: AiInsightsIcon,
				// },
				// {
				// 	id: 6,
				// 	text: 'Cheeni Finance',
				// 	onclick: () => {
				// 		navigate(`/restaurants/${branchId}/finance`);
				// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 	},
				// 	path: `/finance`,
				// 	icon: FinanceIcon,
				// },
			];
		} else {
			navListData = [
				// {
				//id :1,
				// 	text: 'Dashboard',
				// 	onclick: () => {
				// 		navigate(`/dashboard`);
				// 		setOrderBook(false);
				// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 	},
				// 	path: `/dashboard`,
				// 	icon: DashboardIcon,
				// },
				{
					id: 2,
					text: 'Chats',
					onclick: () => {
						navigate(`/suppliers/${branchId}`);
						setOrderBook(false);
						if (isMobileWidth) toggleDrawerOnMobileWidth();
					},
					path: `/suppliers`,
					icon: MessageIcon,
				},
				{
					id: 3,
					text: 'Master Catalog',
					onclick: () => {
						navigate(`/suppliers/${branchId}/productCatalog?page=1&limit=25`);
						if (isMobileWidth) toggleDrawerOnMobileWidth();
					},
					path: `/productCatalog`,
					icon: ProductIcon,
				},
				{
					id: 4,
					text: 'Customer Catalogs',
					onclick: () => {
						navigate(`/suppliers/${branchId}/catalogs`);
						if (isMobileWidth) toggleDrawerOnMobileWidth();
					},
					path: `/catalogs`,
					icon: CatalogsIcon,
				},
				{
					id: 5,
					text: 'Orders',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/suppliers/${branchId}/orders/allOrders`);
						}
						// if (isMobileWidth) toggleDrawerOnMobileWidth();
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/orders`,
					icon: ShoppingCartIcon,
					showOptions: isOptionActive('orders'),
					openNestedMenu: isOptionActive('orders'),
					nestedList: [
						{
							id: 1,
							text: 'All Orders',
							onclick: () => {
								navigate(`/suppliers/${branchId}/orders/allOrders`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('orders/allOrders'),
							path: `/orders`,
						},
						{
							id: 2,
							text: 'Accept Delivery',
							onclick: () => {
								navigate(`/suppliers/${branchId}/orders/acceptDelivery`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('orders/acceptDelivery'),
							path: `/orders`,
						},
					],
				},
				// {
				// 	//temporary
				// 	id: 14,
				// 	text: 'Marketplace',
				// 	onclick: (event: any) => {
				// 		navigate(`/suppliers/${branchId}/marketplace`);
				// 	},
				// 	path: `/marketplace`,
				// 	icon: MarketplaceIcon,
				// },
				{
					id: 6,
					text: 'Settings',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/suppliers/${branchId}/settings/general`);
						}
						// if (isMobileWidth) toggleDrawerOnMobileWidth();
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/settings`,
					icon: SettingsIcon,
					showOptions: isOptionActive('settings/'),
					openNestedMenu: isOptionActive('settings/'),
					nestedList: [
						{
							id: 1,
							text: 'General Settings',
							onclick: () => {
								navigate(`/suppliers/${branchId}/settings/general`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('general'),
							path: `/settings`,
						},
						{
							id: 2,
							text: 'Supplier Settings',
							onclick: () => {
								navigate(`/suppliers/${branchId}/settings/branch`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('settings/branch'),
							path: `/settings`,
						},
						{
							id: 3,
							text: 'Teams & Permissions',
							onclick: () => {
								navigate(`/suppliers/${branchId}/settings/teams&permissions?page=1&limit=25`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('teams') || isOptionActive('permissions'),
							path: `/settings`,
						},
						// {
						// 	id: 4,
						// 	text: 'Restaurant Approval List',
						// 	onclick: () => {
						// 		navigate(`/suppliers/${branchId}/settings/tallyApproval`);
						// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
						// 	},
						// 	active: isOptionActive('tallyApproval'),
						// 	path: `/settings`,
						// },
						{
							id: 5,
							text: 'Integrations',
							onclick: () => {
								navigate(`/suppliers/${branchId}/settings/integrations`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('integrations'),
							path: `/settings`,
						},
					],
				},
				{
					id: 8,
					text: 'Rewards',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/suppliers/${branchId}/rewards/dashboard`);
						}
						// if (isMobileWidth) toggleDrawerOnMobileWidth();
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/rewards`,
					icon: ShoppingCartIcon,
					showOptions: isOptionActive('rewards'),
					openNestedMenu: isOptionActive('rewards'),
					nestedList: [
						{
							id: 1,
							text: 'Rewards Dashboard',
							onclick: () => {
								navigate(`/suppliers/${branchId}/rewards/dashboard`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('rewards/dashboard'),
							path: `/rewards`,
						},
						{
							id: 2,
							text: 'Rewards Settings',
							onclick: () => {
								navigate(`/suppliers/${branchId}/rewards/settings`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('rewards/settings'),
							path: `/rewards`,
						},
						{
							id: 3,
							text: 'Customer Wallet',
							onclick: () => {
								navigate(`/suppliers/${branchId}/rewards/customer-wallet`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('rewards/customer-wallet'),
							path: `/rewards`,
						},
					],
				},
				{
					id: 9,
					text: 'Customers',
					onclick: (event: any, id: number) => {
						setNavListData((prevState: any) => {
							return prevState?.map((item: any) => {
								if (item?.id === id) {
									return { ...item, openNestedMenu: !item?.openNestedMenu };
								}
								return item;
							});
						});
						if (!isMobileWidth) {
							navigate(`/suppliers/${branchId}/customers/list`);
						}
						// if (isMobileWidth) toggleDrawerOnMobileWidth();
						// if (!isDrawerOpen) {
						// 	handleMenuClick(event);
						// }
					},
					path: `/customers`,
					icon: ShoppingCartIcon,
					showOptions: isOptionActive('customers'),
					openNestedMenu: isOptionActive('customers'),
					nestedList: [
						{
							id: 1,
							text: 'Customers List',
							onclick: () => {
								navigate(`/suppliers/${branchId}/customers/list`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('customers/list'),
							path: `/customers`,
						},
						{
							id: 2,
							text: 'Credit Limit',
							onclick: () => {
								navigate(`/suppliers/${branchId}/customers/creditLimit`);
								if (isMobileWidth) toggleDrawerOnMobileWidth();
							},
							active: isOptionActive('customers/creditLimit'),
							path: `/customers`,
						},
					],
				},
				// {
				// 	id: 7,
				// 	text: 'Insights by AI',
				// 	onclick: () => {
				// 		navigate(`/suppliers/${branchId}/ai-insights`);
				// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 	},
				// 	path: `/ai-insights`,
				// 	icon: AiInsightsIcon,
				// },
				// {
				// 	id: 8,
				// 	text: 'Cheeni Finance',
				// 	onclick: () => {
				// 		navigate(`/suppliers/${branchId}/finance`);
				// 		if (isMobileWidth) toggleDrawerOnMobileWidth();
				// 	},
				// 	path: `/finance`,
				// 	icon: FinanceIcon,
				// },
			];
		}
		//@ts-ignore
		setNavListData(navListData);
	}, [navigate, open, isDrawerOpen]);

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			{open && isMobileWidth ? (
				<Backdrop
					sx={{
						zIndex: theme.zIndex.drawer - 1,
						color: '#fff',
						backgroundColor: '#DDDDDDB2',
					}}
					open={true}
					onClick={() => {
						dispatch(setToggleMenu(false));
					}}
				/>
			) : null}
			<Drawer
				variant="permanent"
				open={open}
				PaperProps={{
					sx: {
						justifyContent: 'space-between',
						backgroundColor: '#F7F5F4',
						color: '#7D7D7D',
						borderRight: '2px solid #f1f1f1 !important',
					},
				}}
			>
				<div>
					<DrawerHeader
						sx={{
							justifyContent: open ? 'initial' : 'center',
							paddingLeft: open ? '25px' : '0px 8px',
						}}
					>
						{open && (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '8px',
									marginTop: '20px',
									alignItems: 'center',
								}}
							>
								<img src={logo} alt="logo" width="140px" height="30px" />
								{/* <div
									style={{ fontWeight: 700, fontSize: '18px', color: 'var(--default-text-color)' }}
								>
									Buy Spares
								</div> */}
								{/* <a
									href="/release-notes"
									target="_blank"
									style={{
										fontSize: '12px',
										color: 'var(--color-font-grey)',
										textDecoration: 'underline',
									}}
								>
									Version 1.0.1
								</a> */}
								<div
									style={{
										fontSize: '12px',
										color: 'var(--color-font-grey)',
									}}
								>
									Version 1.0.1
								</div>
							</div>
						)}
						{!open && <img src={logo_2} width="25px" height="25px" alt="logo" />}
						{/* {!open && (
							<div
								style={{ fontWeight: 700, fontSize: '18px', color: 'var(--default-text-color)' }}
							>
								BS
							</div>
						)} */}
					</DrawerHeader>
					<List sx={{ mt: 1 }}>
						{navListData.map((item: any, index: any) => (
							<CustomTooltip
								title={item?.text}
								placement="right"
								disableHoverListener={isDrawerOpen}
							>
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									<ListItem
										key={index}
										disablePadding
										sx={{
											display: 'flex',
											height: '44px',
											my: 1,
										}}
										onClick={(event) => item?.onclick(event, item?.id)}
										onMouseEnter={() => {
											isHover(index);
										}}
										onMouseLeave={() => isHover(-1)}
									>
										{['chats', 'suppliers']?.includes(item?.text?.toLowerCase()) &&
										messageCount > 0 ? (
											<StyledListItemButton
												className="list__item"
												//@ts-ignore
												isActive={isActiveTab(item)}
												sx={{
													// justifyContent: open ? 'initial' : 'center',
													// px: 2.5,
													bg: 'blue',
													display: 'flex',
													justifyContent: 'space-between',
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<ListItemIcon
														sx={{
															minWidth: 0,
															mr: open ? 1 : 'auto',
															justifyContent: 'center',
														}}
													>
														<Badge badgeContent={messageCount} color="primary">
															<item.icon
																stroke={
																	isActiveTab(item)
																		? '#1A1D1F'
																		: handleIsHover(index, hover)
																		? '#1A1D1F'
																		: '#7D7D7D'
																}
															/>
														</Badge>
													</ListItemIcon>
													<ListItemText
														primary={item.text}
														primaryTypographyProps={{ fontSize: '14px !important' }}
														sx={{ opacity: open ? 1 : 0 }}
													/>
												</div>
												{item?.nestedList?.length ? (
													item?.openNestedMenu ? (
														<ArrowUp
															stroke={
																isActiveTab(item)
																	? '#1A1D1F'
																	: handleIsHover(index, hover)
																	? '#1A1D1F'
																	: '#7D7D7D'
															}
														/>
													) : (
														<ArrowDown
															stroke={
																isActiveTab(item)
																	? '#1A1D1F'
																	: handleIsHover(index, hover)
																	? '#1A1D1F'
																	: '#7D7D7D'
															}
														/>
													)
												) : null}
											</StyledListItemButton>
										) : (
											<StyledListItemButton
												className="list__item"
												//@ts-ignore
												isActive={isActiveTab(item)}
												sx={{
													// justifyContent: open ? 'initial' : 'center',
													//px: 2.5,
													bg: 'blue',
													display: 'flex',
													justifyContent: 'space-between',
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<ListItemIcon
														sx={{
															minWidth: 0,
															mr: open ? 1 : 'auto',
															justifyContent: 'center',
															alignItems: 'center',
														}}
													>
														<item.icon
															stroke={
																isActiveTab(item)
																	? '#1A1D1F'
																	: handleIsHover(index, hover)
																	? '#1A1D1F'
																	: '#7D7D7D'
															}
														/>
													</ListItemIcon>
													<ListItemText
														primary={item.text}
														primaryTypographyProps={{ fontSize: '14px !important' }}
														sx={{ opacity: open ? 1 : 0 }}
													/>
												</div>
												{item?.nestedList?.length ? (
													item?.openNestedMenu ? (
														<IconButton className="arrow" onClick={() => {}}>
															<ArrowUp
																stroke={
																	isActiveTab(item)
																		? '#1A1D1F'
																		: handleIsHover(index, hover)
																		? '#1A1D1F'
																		: '#7D7D7D'
																}
															/>
														</IconButton>
													) : (
														<IconButton className="arrow" onClick={() => {}}>
															<ArrowDown
																stroke={
																	isActiveTab(item)
																		? '#1A1D1F'
																		: handleIsHover(index, hover)
																		? '#1A1D1F'
																		: '#7D7D7D'
																}
															/>
														</IconButton>
													)
												) : null}
											</StyledListItemButton>
										)}
									</ListItem>

									{open ? (
										item?.nestedList?.length && item?.openNestedMenu ? (
											<StyledNestedListContainer>
												{item?.nestedList?.map((list: any, index: number) => {
													return (
														<StyledNestedListItem
															key={index}
															onClick={list?.onclick}
															//@ts-ignore
															active={list?.active}
														>
															{list?.text}
														</StyledNestedListItem>
													);
												})}
											</StyledNestedListContainer>
										) : null
									) : null}

									{/* <CustomMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} onClose={handleClose}>
											{item?.nestedList?.map((list: any) => {
												return (
													<StyledMenuItem active={list?.active} onClick={list?.onclick}>
														{list?.text}
													</StyledMenuItem>
												);
											})}
										</CustomMenu> */}
								</div>
							</CustomTooltip>
						))}
					</List>
				</div>
				{isMobileWidth ? null : (
					<DrawerControllers>
						{!open ? (
							<IconButton
								className="chevron__icon"
								color="inherit"
								aria-label="open drawer"
								onClick={handleDrawerOpen}
								edge="start"
							>
								<ChevronRightIcon />
							</IconButton>
						) : (
							<IconButton className="chevron__icon chevron__right" onClick={handleDrawerClose}>
								{theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
							</IconButton>
						)}
					</DrawerControllers>
				)}
				<DrawerFooter sx={{ minHeight: '52px !important', px: '0px !important' }}>
					<>
						<CustomDropdown
							isDrawerOpen={open}
							handleDrawerOpen={handleDrawerOpen}
							restaurant={restaurant}
						/>
						{!open ? (
							<div style={{ backgroundColor: '#FDFDFD', borderTop: '1px solid #F1F1F1' }}>
								<img
									src={restaurant?.default?.image || '/placeholderImage.svg'}
									alt={restaurant?.default?.name}
									width={28}
									height={28}
									style={{
										border: '1px solid #EDEDED',
										borderRadius: '50%',
										margin: '12px 18px',
									}}
								/>
							</div>
						) : null}
					</>

					{/* <div className={'navigation__footer'}>
							<>
								<img src={restaurantLogo} alt="restaurant's logo" width="32px" />
								{open && (
									<Typography variant="body2" sx={{ ml: 2, color: '#1A1D1F' }}>
										{currentUser?.companyName}
									</Typography>
								)}
							</>
						</div> */}
				</DrawerFooter>
			</Drawer>
		</Box>
	);
}
